import { useCallback, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { db, type Job } from '../../api/db';
import { EmptyState, ErrorBox, Loading, Page, Tabs, useAsync, useNotice } from '../../components/common';
import { BackupsTab } from './BackupsTab';
import { CopyConfigsTab } from './CopyConfigsTab';
import { jobPath } from './JobPage';
import { JobsTab } from './JobsTab';
import { OverviewTab } from './OverviewTab';
import { SchedulesTab } from './SchedulesTab';
import { SessionsTab } from './SessionsTab';
import { TablesTab } from './TablesTab';
import { AdviceTab } from '../../indexadvisor/pages/AdviceTab';
import { dbScopes, ProtectedChip, ServerPicker, useDbQuery, useSortedServers, type DbTab } from './shared';
import './databases.css';

const TABS: { value: DbTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'sessions', label: 'Sessions' },
  { value: 'tables', label: 'Tables' },
  { value: 'advice', label: 'Index advice' },
  { value: 'backups', label: 'Backups' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'copy', label: 'Copy configs' },
  { value: 'schedules', label: 'Schedules' },
];

/** Tabs that look at one server (the server picker is shown for these). */
const PER_SERVER: DbTab[] = ['overview', 'sessions', 'tables', 'advice', 'backups'];

export function DatabasesPage({ scopes }: { scopes: readonly string[] }) {
  const s = dbScopes(scopes);
  const q = useDbQuery();
  const list = useAsync(() => db.servers(), []);
  const servers = useSortedServers(list.data);
  const notice = useNotice();
  const tab = TABS.some((t) => t.value === q.tab) ? q.tab : 'overview';
  const server = servers.find((x) => x.id === q.server) ?? null;
  const { set } = q;

  // No (or an unknown) server in the URL: take the first one.
  useEffect(() => {
    if (servers.length && !server) set({ server: servers[0].id, db: null }, true);
  }, [servers, server, set]);

  const navigate = useNavigate();
  const openJob = useCallback((id: number) => navigate(jobPath(id)), [navigate]);
  // A started job opens on its own page, where its log streams.
  const onJob = useCallback((job: Job) => openJob(job.id), [openJob]);
  const onServer = (id: string) => set({ server: id, db: null });
  const onDatabase = (d: string) => set({ db: d || null });

  // A job used to open in a drawer (`?job=`); such links now go to its page.
  if (q.job != null) return <Navigate replace to={jobPath(q.job)} />;
  return (
    <Page
      title="Databases"
      description="Server health, sessions, table sizes, backups and copies."
      actions={
        PER_SERVER.includes(tab) && servers.length ? (
          <>
            <ServerPicker servers={servers} value={server?.id ?? ''} onChange={onServer} />
            {server?.protected && <ProtectedChip />}
          </>
        ) : undefined
      }
    >
      <Tabs value={tab} onChange={(t) => set({ tab: t })} options={TABS} />
      <div className="dbx-body" data-tab={tab}>
        <ErrorBox error={list.error} />
        <Loading loading={list.loading && !list.data} />
        {PER_SERVER.includes(tab) && list.data && !servers.length && (
          <EmptyState
            card
            title="No database servers"
            hint="An admin registers Postgres servers under Admin → DB servers."
          />
        )}
        {server && tab === 'overview' && (
          <OverviewTab
            server={server}
            scopes={s}
            onTables={(d) => set({ tab: 'tables', db: d })}
            onJob={onJob}
            notify={notice.success}
          />
        )}
        {server && tab === 'sessions' && (
          <SessionsTab
            server={server}
            database={q.database}
            onDatabase={onDatabase}
            scopes={s}
            notify={notice.success}
          />
        )}
        {server && tab === 'tables' && (
          <TablesTab server={server} database={q.database} onDatabase={onDatabase} />
        )}
        {server && tab === 'advice' && (
          <AdviceTab server={server} database={q.database} onDatabase={onDatabase} />
        )}
        {server && tab === 'backups' && (
          <BackupsTab
            server={server}
            database={q.database}
            onDatabase={onDatabase}
            scopes={s}
            onOpenJob={openJob}
          />
        )}
        {tab === 'jobs' && <JobsTab servers={servers} onOpen={openJob} refreshKey={0} />}
        {tab === 'copy' && <CopyConfigsTab servers={servers} scopes={s} onJob={onJob} />}
        {tab === 'schedules' && (
          <SchedulesTab servers={servers} scopes={s} onJob={onJob} onOpenJob={openJob} />
        )}
      </div>
      {notice.element}
    </Page>
  );
}
